import React from "react";
import { formatISTDateTime } from "../../lib/admin-time";

export function acquisitionFor(user) {
  const value = user?.acquisition ?? user?.data?.acquisition;
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

export function channelLabel(channel) {
  return ({ paid_search: "Paid search", paid_social: "Paid social", paid_other: "Other paid", organic_search: "Organic search", organic_social: "Organic social", email: "Email", referral: "Referral", direct_or_unknown: "Direct or unknown" })[channel] || channel || "Not captured";
}

export function parameterText(parameters) {
  return Object.entries(parameters || {}).filter(([, value]) => typeof value === "string")
    .map(([key, value]) => `${key}: ${value}`).join("\n");
}

export function csvCell(value) {
  let text = String(value ?? "");
  // Attribution is visitor-supplied; export it as text rather than spreadsheet formulas.
  if (/^[\s]*[=+@-]/.test(text)) text = "'" + text;
  return `"${text.replace(/"/g, '""')}"`;
}

export function AcquisitionText({ value }) {
  return <span className="block min-w-[180px] max-w-xs whitespace-pre-wrap break-words text-xs text-slate-600">{typeof value === "string" && value ? value : "Not captured"}</span>;
}

function Field({ label, value }) {
  return <div className="min-w-0"><dt className="text-xs font-medium text-slate-500 mb-1">{label}</dt><dd className="text-sm text-slate-800 whitespace-pre-wrap break-words">{value || "Not captured"}</dd></div>;
}

function Visit({ title, visit }) {
  return <section className="min-w-0 rounded-lg border border-slate-200 p-4 bg-slate-50/60">
    <h3 className="text-sm font-semibold text-slate-900 mb-4">{title}</h3>
    {visit ? <dl className="space-y-4">
      <Field label="Channel" value={channelLabel(visit.channel)} />
      <Field label="Visited at" value={formatISTDateTime(visit.occurred_at)} />
      <Field label="Landing page" value={visit.landing_page} />
      <Field label="Referrer" value={visit.referrer} />
      <Field label="Referring domain" value={visit.referring_domain} />
      <Field label="UTM parameters" value={parameterText(visit.utm)} />
      <Field label="Ad click IDs" value={parameterText(visit.click_ids)} />
      {visit.truncated && <p className="text-xs text-amber-700">Some values were shortened during collection.</p>}
    </dl> : <p className="text-sm text-slate-500">No visit recorded.</p>}
  </section>;
}

export default function UserAcquisition({ acquisition }) {
  return <section aria-label="Signup attribution" className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 whitespace-normal">
    <div><h2 className="text-lg font-semibold text-slate-900">Signup attribution</h2><p className="text-sm text-slate-500 mt-1">The original visit, latest non-direct visit, and visit that led to signup. Times are in IST.</p></div>
    {!acquisition?.first_touch ? <p className="text-sm text-slate-500">No signup attribution was captured for this user. Older signups and visits where browser storage was unavailable may have no data.</p> : <>
      <dl className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Field label="Signup page" value={acquisition.signup_page} />
        <Field label="Signed up at" value={formatISTDateTime(acquisition.signup_at)} />
        <Field label="Captured at" value={formatISTDateTime(acquisition.captured_at)} />
        <Field label="Saved at" value={formatISTDateTime(acquisition.saved_at)} />
        <Field label="Attribution window" value={acquisition.attribution_window_days ? `${acquisition.attribution_window_days} days` : null} />
        <Field label="Collection method" value={acquisition.collection_method === "first_party_browser" ? "First-party browser" : acquisition.collection_method} />
        <Field label="Anonymous visitor ID" value={acquisition.anonymous_id} />
        <Field label="Record version" value={acquisition.version ? String(acquisition.version) : null} />
      </dl>
      {acquisition.truncated && <p className="text-sm text-amber-700">Some tracking values were shortened to fit browser storage.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Visit title="First visit" visit={acquisition.first_touch} />
        <Visit title="Last non-direct visit" visit={acquisition.last_non_direct_touch} />
        <Visit title="Signup visit" visit={acquisition.signup_session} />
      </div>
    </>}
  </section>;
}
